// Vendor
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Components
import Button, { ButtonColor, ButtonVariant } from 'src/components/atoms/Button';
import { Row } from 'src/components/atoms/Row';
import Text, { TextVariant } from 'src/components/atoms/Text';
import TeamCard from 'src/components/molecules/TeamCard/TeamCard';

// Hooks

import { EntryLogsHistory } from 'src/pages/MyWorkPage/components/EntryLogsHistory';

import {
  BorderContainer,
  CallBox,
  CancelButton,
  StyledRow,
  StyledText,
  SubmissionRequestedText,
  TeamBox
} from '../styles';
import { LoaderCallBox } from './LoaderCallBox';
import { LoaderTeamCard } from './LoaderTeamCard';
import SpecialtyQueueCount from './SpecialtyQueueCount';

import { ConfirmationModal } from 'src/components/molecules/ConfirmationModal';

// Redux
import { selectors } from 'src/features/auth/authSlice';
import useRole from 'src/features/auth/hooks/useUserRoles';
import {
  buildReviewRoute,
  selectors as myWorkSelectors,
  useEntryAssignment
} from 'src/features/myWork';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';

const ReviewTab = ({ isLoading }: { isLoading: boolean }): JSX.Element => {
  const navigate = useNavigate();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const user = useSelector(selectors.user);
  const isLoadingSubmissionRequest = useSelector(myWorkSelectors.isLoadingSubmissionRequest);
  const isLoadingCancelRequest = useSelector(myWorkSelectors.isLoadingCancelRequest);
  const assignedEntry = useSelector(myWorkSelectors.assignedEntry);
  const requestEnqueued = useSelector(myWorkSelectors.requestEnqueued);

  const { isAnalyst } = useRole();

  const userFdeManagerName =
    user?.team?.fde_manager &&
    `${user.team.fde_manager?.first_name} ${user.team.fde_manager?.last_name}`;

  const { cancelRequest, requestEntry } = useEntryAssignment();
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);

  if (user && !user.team?.id) {
    return (
      <BorderContainer>
        <Row gap={1} direction="column">
          <Text variant={TextVariant.h2}>You haven&apos;t been assigned to a Team</Text>
          <Text variant={TextVariant.h4}>Please reach out the FDE Manager to begin work.</Text>
        </Row>
      </BorderContainer>
    );
  }

  if (assignedEntry) {
    return (
      <div>
        <Row justify="space-between" gap={1} alignItems="center">
          <CallBox>
            {isLoading ? (
              <LoaderCallBox />
            ) : (
              <Row justify="space-between" gap={4}>
                <Row.Col size="65%">
                  <Text variant={TextVariant.h3}>Submission pending</Text>
                  <StyledText variant={TextVariant.normal} name="request-submission-text">
                    You have an active submission, please continue to complete it.
                  </StyledText>
                </Row.Col>
                <Row.Col size="35%" alignContent="center">
                  <Button
                    name="continue-reviewing"
                    variant={ButtonVariant.contained}
                    color={ButtonColor.primary}
                    onClick={() => {
                      const route = buildReviewRoute(assignedEntry);

                      if (route) {
                        navigate(route);
                      } else {
                        showSnackbar(
                          VariantType.error,
                          'The assigned review is missing its route information',
                          SnackTypes.none
                        );
                      }
                    }}
                  >
                    Continue Reviewing
                  </Button>
                </Row.Col>
              </Row>
            )}
          </CallBox>
          <TeamBox>
            {isLoading ? (
              <LoaderTeamCard />
            ) : (
              <TeamCard
                teamName={user?.team?.name}
                timezone={user?.team?.timezone}
                fdeManagerName={userFdeManagerName}
              />
            )}
          </TeamBox>
        </Row>
        {isAnalyst && <EntryLogsHistory />}
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" gap={1} alignItems="center">
        <CallBox>
          {isLoading && !requestEnqueued ? (
            <LoaderCallBox />
          ) : (
            <Row justify="space-between" gap={4}>
              <Row.Col size="65%">
                <StyledText variant={TextVariant.normal} name="request-submission-text">
                  Request a new submission to continue to review. A new submission will be sent as
                  soon as it becomes available.
                </StyledText>
                <SpecialtyQueueCount />
              </Row.Col>
              <Row.Col size="35%" alignContent="center">
                <Button
                  name="ask-for-submission"
                  variant={ButtonVariant.contained}
                  color={ButtonColor.primary}
                  onClick={requestEntry}
                  isDisabled={isLoadingSubmissionRequest || requestEnqueued}
                  isLoading={isLoadingSubmissionRequest}
                >
                  Request Submission
                </Button>
                {requestEnqueued ? (
                  <StyledRow>
                    <SubmissionRequestedText variant={TextVariant.small} color="primary">
                      Your request has been submitted. A new submission will be uploaded as soon as
                      it becomes available.
                    </SubmissionRequestedText>
                    <CancelButton
                      onClick={() => setShowCancelConfirmationModal(true)}
                      name="cancel-request"
                      variant={ButtonVariant.ghost}
                      color={ButtonColor.cancel}
                      isDisabled={isLoadingCancelRequest}
                      isLoading={isLoadingCancelRequest}
                    >
                      Cancel Request
                    </CancelButton>
                  </StyledRow>
                ) : null}
              </Row.Col>
            </Row>
          )}
        </CallBox>
        <TeamBox>
          {isLoading && !requestEnqueued ? (
            <LoaderTeamCard />
          ) : (
            <TeamCard
              teamName={user?.team?.name}
              timezone={user?.team?.timezone}
              fdeManagerName={userFdeManagerName}
            />
          )}
        </TeamBox>
      </Row>
      {isAnalyst && <EntryLogsHistory />}
      <ConfirmationModal
        confirmationAction={cancelRequest}
        showConfirmationModal={showCancelConfirmationModal}
        setShowConfirmationModal={setShowCancelConfirmationModal}
        modalTitle=""
        confirmButtonLabel="Yes, continue"
        cancelButtonLabel="Go back"
        modalText="Canceling your request means you won't be auto assigned a new submission. You'll need to submit a new request, which will go to the end of the queue."
      />
    </div>
  );
};

export default ReviewTab;
